import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import moment from 'moment';

export const getKbFeedback = createAsyncThunk(
  'kbFeedback/getKbFeedback',
  async ({ business_id, group_id, page, limit, search }, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;

      if (!business_id) {
        throw new Error('Business ID not found');
      }
      if (!group_id) {
        throw new Error('Group ID not found');
      }

      let url = `${ROOT_URL}/kb/feedback`;
      const params = new URLSearchParams();

      params.append('business_id', business_id);
      params.append('group_id', group_id);
      params.append('page', page);
      params.append('limit', limit);

      if (search) {
        params.append('search', search); // Assuming 'search' is the query param
      }

      url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching KB feedback:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch feedback'
      );
    }
  }
);

function mapFeedbackRecord(feedback) {
  if (!feedback || !feedback._id) return null;
  return {
    ...feedback,
    id: feedback._id,
    createdBy: feedback.authorId?.full_name || 'Unknown',
    article: feedback.articleId?.title || 'Unknown',
    updated: feedback.updatedAt ? moment(feedback.updatedAt).fromNow() : 'Unknown',
    rating: feedback.rating || 0,
    comment: feedback.comment || '',
  };
}

/** GET single feedback (e.g. deep link from notification when not on current list page). */
export const getKbFeedbackById = createAsyncThunk(
  'kbFeedback/getKbFeedbackById',
  async ({ business_id, group_id, feedbackId }, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;

      if (!business_id || !feedbackId) {
        throw new Error('Missing business or feedback id');
      }

      const params = new URLSearchParams({ business_id });
      if (group_id) params.append('group_id', group_id);

      const url = `${ROOT_URL}/kb/feedback/${feedbackId}?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const raw =
        response.data?.data?.feedback ||
        response.data?.data ||
        response.data?.feedback ||
        response.data;
      const mapped = mapFeedbackRecord(raw);
      if (!mapped) {
        throw new Error('Feedback not found');
      }
      return mapped;
    } catch (error) {
      console.error('Error fetching KB feedback by id:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to load feedback'
      );
    }
  }
);

const initialState = {
  loading: true,
  error: null,
  feedbacks: [],
  totalCount: 0,
  totalPages: 0,
  currentPage: 0,
  lastFetchedGroupId: null,
  activeRequestId: null,
};

const kbFeedbackSlice = createSlice({
  name: 'kbFeedback',
  initialState,
  reducers: {
    resetKbFeedback: (state) => {
      return initialState;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKbFeedback.pending, (state, action) => {
        state.activeRequestId = action.meta.requestId;
        const groupId = action.meta?.arg?.group_id ?? null;
        const sameGroup =
          state.lastFetchedGroupId != null &&
          String(state.lastFetchedGroupId) === String(groupId);
        const hasData = !!state.feedbacks?.length || !!state.totalCount;
        state.loading = !sameGroup || !hasData;
        state.error = null;
      })
      .addCase(getKbFeedback.fulfilled, (state, action) => {
        if (state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
          return;
        }
        state.loading = false;
        state.error = null;
        state.lastFetchedGroupId = action.meta?.arg?.group_id ?? null;

        const data = action.payload.data || {};
        const feedbacksList = data.feedbacks || [];
        const pagination = data.pagination || {};

        state.feedbacks = feedbacksList
          .map((feedback) => mapFeedbackRecord(feedback))
          .filter(Boolean);

        state.totalCount = pagination.total || 0;
        state.totalPages = pagination.totalPages || 0;
        state.currentPage = pagination.page || 0;
      })
      .addCase(getKbFeedback.rejected, (state, action) => {
        if (state.activeRequestId && action.meta.requestId !== state.activeRequestId) {
          return;
        }
        state.loading = false;
        state.error = action.payload;
        state.feedbacks = [];
        state.totalCount = 0;
      });
  }
});

export const { resetKbFeedback, clearError } = kbFeedbackSlice.actions;

export default kbFeedbackSlice.reducer;